/**
 * Control descriptors for the "mirrored" remote-mic keyboard.
 *
 * A screen that opts into mirroring (via the `Nav.*` wrappers) describes each of its
 * navigable controls with one of these descriptors. The descriptor is derived from the
 * same props that render the on-screen control, so the phone and the screen cannot drift
 * (single source of truth) — the one exception is `label`, which a screen may override
 * with a shorter `remoteLabel` since the phone has much less horizontal room than a menu.
 *
 * The `type` discriminant is exhaustively handled on the phone via `assertNever`, so adding
 * a new control type here without a matching remote renderer fails the TypeScript build.
 */

import type { remoteButtonIcons } from '~/routes/keyboard-help/remote-button-icons';

/**
 * A run of controls that belong together because of what they are ABOUT, rather than because of
 * what type they are - the four player slots of the mic setup, say. Optional, so a phone on an
 * older build ignores it and renders the flat list it renders today.
 */
export interface ControlGroup {
  id: string;
  label: string;
  /** Drawn as the card's outline and heading, so a slot reads in its player's colour. */
  colour?: string;
  /**
   * The slot this group stands for, when it stands for one. The phone marks its own group and sorts
   * it first by comparing this with the player number it was assigned. NOT an `isSelf` flag: one
   * layout is published to every connected phone, so a flag decided on the host would mark the same
   * slot "YOU" on all of them.
   */
  player?: number;
}

/** Fields injected by `useKeyboardNav` (not provided by the screen author). */
interface ControlMeta {
  name: string;
  disabled?: boolean;
  group?: ControlGroup;
}

/**
 * One choice in a `select`. `disabled` is what stops a slot taking a microphone another slot is
 * already singing into, and `note` says where it went - both optional, so a phone on an older build
 * ignores them and renders the flat list of labels it renders today.
 */
export interface ControlOption {
  value: string;
  label: string;
  disabled?: boolean;
  note?: string;
  kind?: 'phone' | 'computer' | 'mic';
  // Set on an option that is a connected phone, so that phone can find itself in the list.
  micId?: string;
}

/**
 * Marks a button's semantic role, so the remote can add a cue the television does not need - a
 * leading back arrow, or a confirmation. `destructive` says the button takes something away; the
 * phone asks first only when the thing being taken away is its own (see `keyboard.tsx`), because on
 * the television whoever holds the remote is never the one being removed. An older phone tests for
 * `'back'` alone, so an unknown role renders as an ordinary button.
 */
export type ButtonVariant = 'back' | 'destructive';

/**
 * Names of the icons the remote knows how to draw on the RIGHT of a mirrored button. The host
 * references one by name (the icon glyph itself lives on the phone, see `remoteButtonIcons` in
 * `remote-button-icons.ts` — the single source of truth this type is derived from) so nothing but a
 * short string crosses the wire.
 *
 * On a mirrored button the right icon defaults to `'forward'`; a screen may pass another name to
 * override it, or `null` to drop it entirely. The `back` variant ignores this and shows a leading
 * back arrow instead.
 */
export type RemoteButtonIcon = keyof typeof remoteButtonIcons;

export type ControlDescriptor =
  | (ControlMeta & {
      type: 'button';
      label: string;
      variant?: ButtonVariant;
      icon?: RemoteButtonIcon | null;
      /**
       * A second, quieter fact about the row, drawn to the right of the label - "42 plays" beside a
       * singer's name. For what the row IS rather than what pressing it does; a label that needs two
       * sentences wants a shorter label, not a note. Optional, so a phone on an older build simply
       * draws the label it drew before.
       */
      note?: string;
    })
  | (ControlMeta & { type: 'switch'; label: string; value: string })
  | (ControlMeta & { type: 'checkbox'; label: string; checked: boolean })
  // A free-form text field mirrored to the phone (e.g. renaming a high-score). The phone edits the
  // value locally and streams it back through `serverRpc.input.setControlValue`, which the host
  // routes to the same `onChange` the on-screen input uses — so typing on either device stays in sync.
  | (ControlMeta & { type: 'text'; label: string; value: string; placeholder?: string })
  // The game's global input-lag setting. Rendered on the phone as the SAME numeric stepper the remote
  // settings screen uses, wired straight to `serverRpc.settings.setInputLag` (no per-control value
  // channel needed) so it behaves identically wherever it appears. `value` seeds the stepper's display.
  | (ControlMeta & { type: 'input-lag'; label: string; value: number })
  // A locked list of options, of which exactly one is chosen - a microphone picker, say. Shares the
  // `setControlValue` channel with `text`, sending the chosen option's `value` rather than free
  // text, so the host applies it through the same `onValueChange` the on-screen control uses. Kept
  // distinct from `switch` because a switch can only say "next", which is no use for a list a phone
  // cannot see the length of.
  | (ControlMeta & {
      type: 'select';
      label: string;
      value: string;
      options: ControlOption[];
    });

export type ControlType = ControlDescriptor['type'];

/**
 * What a screen author passes (via the `Nav.*` wrappers). The `name`/`focused`/`disabled`
 * meta fields are added by `useKeyboardNav.register`.
 */
export type ControlInput = { group?: ControlGroup } & (
  | { type: 'button'; label: string; variant?: ButtonVariant; icon?: RemoteButtonIcon | null; note?: string }
  | { type: 'switch'; label: string; value: string }
  | { type: 'checkbox'; label: string; checked: boolean }
  | { type: 'text'; label: string; value: string; placeholder?: string }
  | { type: 'input-lag'; label: string; value: number }
  | { type: 'select'; label: string; value: string; options: ControlOption[] }
);

/** Exhaustiveness guard — a compile error here means a control type is unhandled. */
export function assertNever(value: never): never {
  throw new Error(`Unhandled control descriptor: ${JSON.stringify(value)}`);
}

/**
 * The descriptors this build can actually draw.
 *
 * The phone and the host are routinely different builds - the desktop app points phones at the
 * published site, which lags behind whatever the player has installed - so a descriptor naming a
 * control type this build has never heard of is version skew, not corruption. Dropping it leaves
 * the player one control short of the TV; throwing would take the whole microphone down with it.
 */
export const renderableControls = (controls: ControlDescriptor[]): ControlDescriptor[] =>
  controls.filter((control) => {
    if (isValidControl(control)) return true;

    console.warn('Skipping a control this build cannot render', control);
    return false;
  });

/** Runtime sanity check for a single descriptor (defense in depth for the wire payload). */
export function isValidControl(control: ControlDescriptor): boolean {
  if (!control || typeof control.name !== 'string' || !control.name) return false;
  if (typeof control.label !== 'string') return false;
  switch (control.type) {
    case 'button':
      // `icon` is optional; when present it is either a known name (string) or `null` to disable it.
      return control.icon === undefined || control.icon === null || typeof control.icon === 'string';
    case 'switch':
      return typeof control.value === 'string';
    case 'checkbox':
      return typeof control.checked === 'boolean';
    case 'text':
      // `placeholder` is optional; `value` is the current (possibly empty) text.
      return (
        typeof control.value === 'string' &&
        (control.placeholder === undefined || typeof control.placeholder === 'string')
      );
    case 'input-lag':
      return typeof control.value === 'number';
    case 'select':
      // An empty list is valid - a slot can be offered a picker before any device has been probed.
      return (
        typeof control.value === 'string' &&
        Array.isArray(control.options) &&
        control.options.every(
          (option) => option && typeof option.value === 'string' && typeof option.label === 'string',
        )
      );
    default:
      return false;
  }
}
